import type { GraphModification, TemplateEvent, Graph } from "@lr-notitia/notitia-designer/schemas";
import { Ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-designer/client";
import { handleGraphModification } from "./utils";
import { useSymbolsStore } from "../symbols";
import { useGraphStore } from "../graph";

let graphStore: ReturnType<typeof useGraphStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	graphStore = useGraphStore();
	symbolsStore = useSymbolsStore();
}

export const templateEvents = (graph: Ref<Graph>) =>
	computed<TemplateEvent[]>(() => {
		if (!symbolsStore) init();
		return (
			Object.values(graph.value.nodes).filter(
				(node) => node.type === "event" && node.layer === "template"
			) as TemplateEvent[]
		).sort((a, b) =>
			symbolsStore
				.term({ id: a.refersTo, terminology: a.terminology || {} })
				.toLocaleLowerCase()
				.localeCompare(symbolsStore.term({ id: b.refersTo, terminology: b.terminology || {} }).toLocaleLowerCase())
		);
	});
export function templateEvent(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => byId(id, graph),
		add: (conceptId: string) => add(domainId, graph, conceptId),
		update: (event: TemplateEvent) => update(domainId, graph, event),
		remove: (id: string) => remove(domainId, graph, id),
	};
}

function byId(id: string, graph: Ref<Graph>): TemplateEvent | undefined {
	let hit = graph.value.nodes[id];
	return hit && hit.type === "event" && hit.layer === "template" ? (hit as TemplateEvent) : undefined;
}

function add(domainId: Ref<string>, graph: Ref<Graph>, conceptId: string): Promise<GraphModification> {
	if (!graphStore) init();
	return new Promise((resolve, reject) => {
		let concept = graphStore.concept.event.byId(conceptId);
		if (!concept) return reject();
		api()
			.domain(domainId.value)
			.template()
			.events()
			.post({ concept: concept })
			.then((data) => {
				handleGraphModification(data);
				resolve(data);
			});
	});
}
function update(domainId: Ref<string>, graph: Ref<Graph>, event: TemplateEvent): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[event.id]) return reject();
		api()
			.domain(domainId.value)
			.graphs()
			.patch({ [event.id]: event })
			.then((data) => {
				handleGraphModification(data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	return new Promise((resolve, reject) => {
		if (!graph.value.nodes[id]) return reject();
		api()
			.domain(domainId.value)
			.graphs()
			.patch({ [id]: null })
			.then((data) => {
				handleGraphModification(data);
				resolve(data);
			})
			.catch(() => reject());
	});
}
